import { crossBrowserExitFullscreen } from 'util/fullscreen';
import { useCallback, useMemo, useRef } from 'react';
import { LocalVideoTrack, Participant, RemoteVideoTrack } from 'livekit-client';
import isNil from 'lodash/isNil';
import { observer } from 'mobx-react-lite';
import { FloatActiveSpeaker, StageFooter } from 'features/Room/components';
import { ScreenShare } from 'features/ScreenShare';
import { selectLocalParticipant, selectRecentSpeaker, selectRecentSpeakerId } from 'state/selectors';
import { useDoubleClick } from 'ui/hooks/useDoubleClick';
import { useShowOnMovingPointer } from 'ui/hooks/useShowOnMovingPointer';
import { useRegisterAlwaysVisibleRef } from 'ui/hooks/useRegisterAlwaysVisibleRef';
import { StageProps } from '../types';
import {
    ExitFullscreenButton,
    FocusFloatActiveSpeakerButton,
    FocusFloatActiveSpeakerWindow,
    FooterLayout,
    FullscreenMainLayout,
    FullscreenStageLayout,
} from './view';

type FullscreenShareStageProps = StageProps & {
    participants: Participant[];
    screenTrack: LocalVideoTrack | RemoteVideoTrack;
};

export const FullscreenShareStage = observer(
    ({ roomState, controlRenderer, onLeave, hasRecorder, participants, screenTrack }: FullscreenShareStageProps) => {
        const handleExitFullscreen = useCallback(() => {
            crossBrowserExitFullscreen();
        }, []);

        const ref = useDoubleClick<HTMLDivElement>({
            callback: handleExitFullscreen,
        });

        const { isInterfaceHidden, registerAlwaysVisibleElement } = useShowOnMovingPointer({
            ref,
        });

        const footerRef = useRef<HTMLElement | null>(null);
        useRegisterAlwaysVisibleRef(footerRef, registerAlwaysVisibleElement);
        const isFooterHidden = isInterfaceHidden || hasRecorder;

        const recentSpeakerId = selectRecentSpeakerId();
        const recentSpeaker = useMemo(() => {
            const recentSpeaker = selectRecentSpeaker();
            if (!recentSpeaker) {
                return selectLocalParticipant();
            }

            return recentSpeaker;
        }, [recentSpeakerId]);

        return (
            <FullscreenStageLayout>
                <FullscreenMainLayout ref={ref} $isInterfaceHidden={isInterfaceHidden}>
                    <ScreenShare track={screenTrack} isAuthorShown={!isInterfaceHidden} />
                    {!isNil(recentSpeaker) && (
                        <FloatActiveSpeaker
                            participant={recentSpeaker}
                            openButtonHidden={isInterfaceHidden}
                            ButtonComponent={FocusFloatActiveSpeakerButton}
                            WindowComponent={FocusFloatActiveSpeakerWindow}
                        />
                    )}
                    <ExitFullscreenButton
                        onClick={handleExitFullscreen}
                        isFullscreenActive
                        isHidden={isInterfaceHidden}
                    />
                </FullscreenMainLayout>
                {/* Кнопки управления будут скрываться в fullscreen и показываться только при наведении */}
                <FooterLayout ref={footerRef} $hidden={isFooterHidden}>
                    {!hasRecorder && (
                        <StageFooter
                            roomState={roomState}
                            controlRenderer={controlRenderer}
                            onLeave={onLeave}
                            participants={participants}
                            isInterfaceHidden={isInterfaceHidden}
                            registerAlwaysVisibleElement={registerAlwaysVisibleElement}
                        />
                    )}
                </FooterLayout>
            </FullscreenStageLayout>
        );
    },
);

FullscreenShareStage.displayName = 'FullscreenShareStage';
